import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  IconButton,
  Typography,
  Grid,
  Avatar,
  Slide,
  Card,
  CardMedia,
  CardContent,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import CircleIcon from '@mui/icons-material/Circle';
import { pdfjs } from 'react-pdf';
import DialogPreview from '../../components/dialog/alert-detail/preview';
import { checkValueTypeGlobal, capitalizeFirstLetterGlobal, determineTypeGlobal } from '../../components/helper';
import Nopreview from '../../style/no-preview.png';

// Try jsdelivr with legacy build path for version 5.x
// Legacy build should be more compatible with react-pdf
const pdfjsVersion = pdfjs.version || '5.4.296';
const workerUrl = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsVersion}/legacy/build/pdf.worker.min.mjs`;
pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);
const TRANSITION_DURATION = { appear: 400, enter: 300, exit: 200 };

function MyComponent({ valid = false, invalid = false, timeEpoch, isEndList = false, highlight }) {
  const date = new Date(timeEpoch);
  const options = { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Jakarta' };
  const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);

  const hours = date.getHours();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = (hours % 12 || 12).toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const formattedTime = `${formattedHours}:${minutes} ${ampm}`;

  return (
    <>
      <div className="div" style={{ borderBottom: highlight ? '2px solid #0048f9f2' : '' }}>
        <div className="div-2">
          <div className="div-3">
            <div className="div-4">
              {valid && invalid ? (
                <span>
                  <img loading="lazy" src={'/assets/X.png'} alt="line Logo" className="img" />
                  <img loading="lazy" src={'/assets/close.png'} alt="line Logo" className="img" />
                </span>
              ) : (
                <img
                  loading="lazy"
                  src={valid ? '/assets/X.png' : '/assets/close.png'}
                  alt="line Logo"
                  className="img"
                />
              )}
            </div>
            {isEndList ? '' : <div className="div-5" />}
          </div>
          <div className="div-6">
            <img src="/assets/Line.png" alt="line Logo" className="w-100" />
            <div className="div-7">
              <div className="div-8">{formattedDate}</div>
              <div className="div-9">{formattedTime}</div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .div {
          display: flex;
          gap: 4px;
        }
        .div-2 {
          display: flex;
          flex-grow: 1;
          flex-basis: 0%;
          flex-direction: column;
        }
        .div-3 {
          display: flex;
          justify-content: space-between;
          gap: 4px;
        }
        .div-4 {
          justify-content: center;
          align-items: center;
          border-radius: 131.413px;
          border: 1.027px solid var(--Stroke-Light-Base, #e5e5e7);
          box-shadow: 0px 1.027px 2.053px 0px rgba(16, 24, 40, 0.04);
          background-color: var(--Background-Light-Base-Main, #fff);
          display: flex;
          aspect-ratio: 1;
          flex-direction: column;
          width: 44px;
          height: 44px;
          padding: 0 12px;
        }
        .img {
          aspect-ratio: 0.95;
          object-fit: contain;
          object-position: center;
          width: 100%;
          overflow: hidden;
        }
        .div-5 {
          background-color: #bfeaf8;
          align-self: center;
          width: 55px;
          height: 1px;
          margin: auto 0;
        }
        .div-6 {
          align-items: start;
          display: flex;
          margin-top: 4px;
          justify-content: space-between;
          gap: 8px;
          padding: 0 19px;
        }
        .div-7 {
          align-self: stretch;
          display: flex;
          flex-grow: 1;
          flex-basis: 0%;
          flex-direction: column;
        }
        .div-8 {
          color: var(--Blue-700, #0e7496);
          white-space: nowrap;
          font: 500 11px/127% Roboto, -apple-system, Roboto, Helvetica, sans-serif;
        }
        .div-9 {
          color: var(--Blue-700, #0e7496);
          white-space: nowrap;
          font: 500 11px/127% Roboto, -apple-system, Roboto, Helvetica, sans-serif;
        }
      `}</style>
    </>
  );
}

MyComponent.propTypes = {
  valid: PropTypes.bool,
  invalid: PropTypes.bool,
  timeEpoch: PropTypes.number,
  isEndList: PropTypes.bool,
  highlight: PropTypes.bool,
};

// ---------------------------------
// Helpers (same visual output as yours)
// ---------------------------------
const filterObject = (arr1, arr2) => {
  const pathsToRemove = new Set((arr2 || []).map((obj, _i) => obj.path));
  return (arr1 || []).filter((obj) => !pathsToRemove.has(obj.path));
};

function GenerateObject({ data = {} }) {
  return Object.keys(data || {})?.map((key) => {
    const typevalue = checkValueTypeGlobal(data[key]);
    let tittle = capitalizeFirstLetterGlobal(key);

    // Replace specific titles
    if (tittle === 'FileLeafRef' || tittle === 'File Leaf Ref') {
      tittle = 'Filename';
    } else if (tittle === 'FileRef' || tittle === 'File Ref') {
      tittle = 'File Path';
    } else if (tittle === 'FileDirRef' || tittle === 'File Dir Ref') {
      tittle = 'Folder Directory';
    }

    const value = data[key];

    if (value === null) {
      if (
        key.includes('hash') ||
        key.includes('Hash') ||
        key.includes('HASH') ||
        key === 'bucketname' ||
        key === 'key' ||
        key === 'path' ||
        tittle === 'Bucket Name' ||
        tittle === 'Object Type' ||
        data[key] === 'ap-southeast-4' ||
        (tittle === 'Location' && data[key] === null)
      ) {
        return <span key={key} />;
      }

      return (
        <Grid key={key} item xs={12} container direction={'row'} justifyContent={'space-between'}>
          <Grid item>
            <Typography variant="body2">{tittle}</Typography>
          </Grid>
          <Grid item>
            <Typography variant="body2">{value}</Typography>
          </Grid>
          <TextField
            variant="outlined"
            value={value}
            fullWidth
            InputProps={{
              readOnly: true,
              disableUnderline: true,
            }}
          />
        </Grid>
      );
    }

    if (
      key.includes('hash') ||
      key.includes('Hash') ||
      key.includes('HASH') ||
      key === 'bucketname' ||
      key === 'key' ||
      key === 'path' ||
      tittle === 'BucketName' ||
      tittle === 'ObjectType' ||
      data[key] === 'ap-southeast-4' ||
      tittle === 'Location'
    ) {
      return <span key={key} />;
    }

    return typevalue === 'object' ? (
      <Grid key={key} item container direction={'row'} spacing={2}>
        {determineTypeGlobal(value) === 'array' ? (
          <Grid item container direction={'row'} spacing={2}>
            <Grid item alignSelf={'center'}>
              {' '}
              <CircleIcon style={{ color: '#1EB3D9', fontSize: 10 }} />
            </Grid>
            <Grid item>
              <Typography variant="h5">{tittle}</Typography>
            </Grid>
          </Grid>
        ) : (
          ''
        )}
        {GenerateObject({ data: value })}
      </Grid>
    ) : (
      <Grid key={key} item xs={12} container direction={'row'} justifyContent={'space-between'}>
        <Grid item>
          <Typography variant="body2">{tittle}</Typography>
        </Grid>
        <TextField
          variant="outlined"
          value={value}
          fullWidth
          multiline
          InputProps={{
            readOnly: true,
            disableUnderline: true,
          }}
        />
      </Grid>
    );
  });
}

GenerateObject.propTypes = {
  data: PropTypes.object,
};

const GenerateDialog = React.memo(
  ({
    open,
    onClose,
    t,
    stateTab2,
    record,
    orderlist,
    recorddetail,
    dialogUploadPreview,
    setDialogUploadPreview,
    getToken,
    baseUrl = typeof globalThis !== 'undefined' ? globalThis.REACT_APP_API_PURE : '',
  }) => {
    // -------- state mirrors (only sync when opening) --------
    const [localData, setLocalData] = React.useState(recorddetail || {});
    const [imagePreviewsinside, setImagePreviewsinside] = React.useState([]);
    const [selectedindexinside2, setselectedindexinside2] = React.useState(orderlist?.length || 1);
    const [loading, setLoading] = React.useState(false);

    // -------- DOM refs for height lock & overlay --------
    const contentRef = React.useRef(null);
    const thumbsWrapRef = React.useRef(null);
    const [lockedHeight, setLockedHeight] = React.useState(null);

    // -------- abort / channel + blob URL management --------
    const abortRef = React.useRef(null);
    const reqIdRef = React.useRef(0);
    const urlsRef = React.useRef([]);

    const revokeAll = React.useCallback(() => {
      if (urlsRef.current.length) {
        for (const u of urlsRef.current) {
          try {
            URL.revokeObjectURL(u);
          } catch {}
        }
        urlsRef.current = [];
      }
    }, []);

    const startNewChannel = React.useCallback(
      (opts = {}) => {
        const { keepVisible = false, lockNow = false } = opts;

        if (abortRef.current) {
          abortRef.current.abort();
          abortRef.current = null;
        }
        abortRef.current = new AbortController();
        reqIdRef.current += 1;

        if (lockNow && contentRef.current) {
          const h = contentRef.current.getBoundingClientRect().height;
          setLockedHeight(h);
        }

        revokeAll();

        if (!keepVisible) {
          setImagePreviewsinside([]);
        }

        return { signal: abortRef.current.signal, reqId: reqIdRef.current };
      },
      [revokeAll]
    );

    React.useEffect(
      () => () => {
        if (abortRef.current) abortRef.current.abort();
        revokeAll();
      },
      [revokeAll]
    );

    // -------- data normalizer (handles ".after") --------
    const normalize = React.useCallback((d) => (d?.after ? { data: d.after } : d || {}), []);

    // -------- thumbnails builder (batched -> no flicker) --------
    const fetchImageInside = React.useCallback(
      async (payload, signal, reqId) => {
        const entries = payload?.data && typeof payload.data === 'object' ? Object.entries(payload.data) : [];
        const nextPreviews = [];
        const localUrls = [];

        for (const [filename, file] of entries) {
          if (signal?.aborted || reqId !== reqIdRef.current) break;

          const path = file?.metaData?.storageMetaData?.path;
          if (!path) continue;

          const thumbUrl = `${baseUrl}record/download/file/${encodeURIComponent(
            path.replaceAll('/', '%2F')
          )}/thumbnail`;

          try {
            const res = await fetch(thumbUrl, {
              method: 'GET',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken?.()}` },
              signal,
            });

            if (!res.ok) {
              nextPreviews.push({ url: '', type: 'png', path, filename });
              continue;
            }

            const blob = await res.blob();
            if (signal?.aborted || reqId !== reqIdRef.current) break;

            const objectUrl = URL.createObjectURL(blob);
            localUrls.push(objectUrl);
            nextPreviews.push({ url: objectUrl, type: 'png', path, filename });
          } catch {
            if (signal?.aborted) break;
            nextPreviews.push({ url: '', type: 'png', path, filename });
          }
        }

        if (!signal?.aborted && reqId === reqIdRef.current) {
          urlsRef.current.push(...localUrls);
          setImagePreviewsinside(nextPreviews);
        } else {
          for (const u of localUrls) {
            URL.revokeObjectURL(u);
          }
        }
      },
      [baseUrl, getToken]
    );

    const recordIdFromNamepath = React.useMemo(() => {
      const np = stateTab2?.namepath;
      if (!np) return '';
      const parts = np.split(' > ');
      return parts.at(-1) || '';
    }, [stateTab2?.namepath]);

    // -------- open/switch record: clear immediately, then load --------
    React.useEffect(() => {
      if (!open) return;
      setLocalData(recorddetail || {});
      setselectedindexinside2(orderlist?.length || 1);

      const { signal, reqId } = startNewChannel({ keepVisible: false, lockNow: true });
      const payload = normalize(recorddetail || {});
      setLoading(true);

      (async () => {
        try {
          await fetchImageInside(payload, signal, reqId);
        } finally {
          if (!signal.aborted) setLoading(false);
          requestAnimationFrame(() => setLockedHeight(null));
        }
      })();
    }, [open, recorddetail, orderlist, normalize, startNewChannel, fetchImageInside]);

    // -------- timeline click: keep current thumbnails, lock height, then swap --------
    const handleTimelineClick = React.useCallback(
      async (row, item) => {
        setselectedindexinside2(row + 1);
        if (!item?.recordId || !item?.txInfos?.txId) return;

        const { signal, reqId } = startNewChannel({ keepVisible: true, lockNow: true });
        setLoading(true);

        try {
          const res = await fetch(`${baseUrl}record/compare/${item.recordId}/${item.txInfos?.txId}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken?.()}` },
            signal,
          });
          const raw = await res.json();
          if (signal.aborted || reqId !== reqIdRef.current) return;

          const payload = normalize(raw);
          setLocalData(payload);

          await fetchImageInside(payload, signal, reqId);
        } finally {
          if (!signal.aborted) setLoading(false);
          requestAnimationFrame(() => setLockedHeight(null));
        }
      },
      [baseUrl, getToken, normalize, startNewChannel, fetchImageInside]
    );

    const handleClose = React.useCallback(
      (e, reason) => {
        if (abortRef.current) abortRef.current.abort();
        revokeAll();
        setImagePreviewsinside([]);
        setLoading(false);
        setLockedHeight(null);
        onClose?.(e, reason);
      },
      [onClose, revokeAll]
    );

    const titleValue = stateTab2?.datadetail?.id || '';

    return (
      <Dialog
        minWidth={'lg'}
        open={open}
        fullWidth
        keepMounted
        TransitionComponent={Transition}
        transitionDuration={TRANSITION_DURATION}
        onClose={handleClose}
        slotProps={{ backdrop: { timeout: 220 } }}
        maxWidth={'lg'}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        {}
        {dialogUploadPreview?.open && (
          <DialogPreview
            open={dialogUploadPreview.open}
            handleClose={dialogUploadPreview.handleClose}
            path={dialogUploadPreview.path}
            filename={dialogUploadPreview.filename}
            showAllPages
          />
        )}

        <DialogTitle id="alert-dialog-title">
          {titleValue}
          <IconButton
            aria-label="close"
            onClick={(e) => handleClose(e, 'closeButton')}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'inherit' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            component="div"
            ref={contentRef}
            style={{
              overflowX: 'hidden',
              ...(lockedHeight ? { height: lockedHeight, overflowY: 'auto' } : null),
            }}
          >
            <Grid container direction={'row'} spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.75)' }}>
                  <strong>Record Id:</strong>{' '}
                  <span style={{ wordBreak: 'break-all' }}>
                    {recorddetail?.recordId || orderlist?.at(-1)?.recordId || recordIdFromNamepath || '-'}
                  </span>
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant={'h5'} style={{ color: 'black' }}>
                  {t?.('Historical.1') ?? 'Historical'}
                </Typography>
              </Grid>

              <Grid item alignSelf={'center'}>
                <Avatar sx={{ width: 15, height: 15, backgroundColor: '#EE5020', fontSize: '10px' }}>
                  {record?.unAuthorizedChanges ?? 0}
                </Avatar>
              </Grid>
              <Grid item>
                <Typography variant="caption">{t?.('UnauthorizedChanges.1') ?? 'Unauthorized Changes'}</Typography>
              </Grid>
              <Grid item alignSelf={'center'}>
                <Avatar sx={{ width: 15, height: 15, backgroundColor: '#1EB3D9', fontSize: '10px', marginLeft: 3 }}>
                  {record?.authorizedChanges ?? 0}
                </Avatar>
              </Grid>
              <Grid item>
                <Typography variant="caption">{t?.('AuthorizedChanges.1') ?? 'Authorized Changes'}</Typography>
              </Grid>

              {}
              {Array.isArray(orderlist) && orderlist.length > 0 && (
                <Grid item xs={12} style={{ pointerEvents: loading ? 'none' : 'auto' }}>
                  <div style={{ overflow: 'auto', marginTop: 10, marginBottom: 10, paddingLeft: 20 }}>
                    <Grid item xs={12} container direction="row" style={{ overflowX: 'auto', width: 'max-content' }}>
                      {orderlist.map((item, row) => (
                        <button
                          type="button"
                          key={item?.txInfos?.txId || row}
                          onClick={() => handleTimelineClick(row, item)}
                          aria-label={`Timeline item ${row + 1} of ${orderlist.length}`}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                          }}
                        >
                          {MyComponent({
                            // Align flags with detail-alert: X when valid, close when invalid
                            valid: Boolean(item?.authorized ?? item?.valid ?? false),
                            invalid: Boolean(
                              item?.unauthorized ?? (Array.isArray(item?.invalids) && item.invalids.length > 0)
                            ),
                            timeEpoch: item?.epoch,
                            isEndList: orderlist.length === row + 1,
                            highlight: (selectedindexinside2 || orderlist.length) === row + 1,
                          })}
                        </button>
                      ))}
                    </Grid>
                  </div>
                </Grid>
              )}

              {}
              <Grid item xs={12}>
                <Typography variant={'h5'} style={{ color: 'black' }}>
                  {t?.('RecordfromBlockchain.1') ?? 'Record from Blockchain'}
                </Typography>
              </Grid>

              {}
              <Grid item xs={12}>
                <div ref={thumbsWrapRef} style={{ position: 'relative' }}>
                  {loading && (
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(255,255,255,0.4)',
                        backdropFilter: 'blur(1px)',
                        zIndex: 1,
                      }}
                    >
                      <Typography variant="body2">Loading…</Typography>
                    </div>
                  )}

                  <Grid container direction="row" spacing={2}>
                    {imagePreviewsinside.map((preview, index) => (
                      <Grid item xs={4} key={`${preview.filename}-${index}`}>
                        <div>
                          <Card raised sx={{ maxWidth: 180, margin: '0 auto', padding: '0.1em', position: 'relative' }}>
                            <CardMedia
                              component="img"
                              image={preview.url || Nopreview}
                              alt={`Preview ${index}`}
                              sx={{ padding: '1em 1em 0 1em', objectFit: 'contain', height: 180 }}
                              onClick={() =>
                                setDialogUploadPreview?.((prevState) => ({
                                  ...prevState,
                                  open: !prevState.open,
                                  base64String: preview.url,
                                  type: preview.type,
                                  path: preview.path,
                                  filename: preview.filename,
                                }))
                              }
                            />
                            <CardContent style={{ textAlign: 'center' }}>
                              <Typography variant="caption">{preview.filename}</Typography>
                            </CardContent>

                            {String(preview.filename || '')
                              .toLowerCase()
                              .includes('.pdf') && (
                              <PictureAsPdfIcon
                                fontSize="large"
                                sx={{ position: 'absolute', bottom: 0, right: 0, color: 'red' }}
                              />
                            )}
                            {String(preview.filename || '')
                              .toLowerCase()
                              .includes('.mp3') && (
                              <LibraryMusicIcon
                                fontSize="large"
                                sx={{ position: 'absolute', bottom: 0, right: 0, color: '#1EB3D9' }}
                              />
                            )}
                          </Card>
                        </div>
                      </Grid>
                    ))}
                  </Grid>
                </div>
              </Grid>

              {}
              {localData?.data && (
                <Grid item xs={12} container direction={'row'}>
                  <GenerateObject data={localData.data} />
                </Grid>
              )}
            </Grid>
          </DialogContentText>
        </DialogContent>
      </Dialog>
    );
  }
);

GenerateDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  t: PropTypes.func,
  stateTab2: PropTypes.object,
  record: PropTypes.array,
  orderlist: PropTypes.array,
  recorddetail: PropTypes.array,
  dialogUploadPreview: PropTypes.object,
  setDialogUploadPreview: PropTypes.func,
  getToken: PropTypes.func,
  baseUrl: PropTypes.string,
};

export default GenerateDialog;
